import {
  Component,
  ComponentRef,
  computed,
  inject,
  inputBinding,
  OnInit,
  signal,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { ApiClient } from '@core/services/api-client';
import { JwtDecoderService } from '@core/services/jwt-decoder-service';
import { ProblemModal } from '@features/test-sets/problem-modal/problem-modal';
import { Button } from '@shared/components/button/button';
import { Item } from '@shared/components/item/item';
import { Select, SelectItem } from '@shared/components/select/select';
import { Toast, ToastMessage } from '@shared/components/toast/toast';
import { AllProblems, Problem } from '@shared/models/problem';
import { TestSetFormData } from '@shared/models/test-sets';

@Component({
  selector: 'app-create-page',
  imports: [Select, Item, Button, ProblemModal, RouterLink],
  templateUrl: './create-page.html',
})
export class CreatePage implements OnInit {
  // Injector
  private readonly apiClient = inject(ApiClient);
  private readonly router = inject(Router);
  private readonly jwtDecoder = inject(JwtDecoderService);

  // Constants
  private readonly DIFFICULTY_OPTIONS: SelectItem[] = [
    { value: 'senior', label: 'Senior' },
    { value: 'mid', label: 'Middle' },
    { value: 'junior', label: 'Junior' },
  ];

  // State
  readonly isLoading = signal<boolean>(true);
  readonly showProblemModal = signal<boolean>(false);

  // Problem
  readonly allProblems = signal<Problem[]>([]);
  readonly selectedProblems = signal<Problem[]>([]);

  // Selector
  readonly selectItems = signal<SelectItem[]>(this.DIFFICULTY_OPTIONS);
  readonly selectedDifficulty = signal<SelectItem>(this.DIFFICULTY_OPTIONS[0]);

  // User
  readonly userId = computed(() => {
    const token = localStorage.getItem('accessToken');
    return token ? this.jwtDecoder.getUserId(token) : null;
  });

  // Toast functionality
  private toastRef: ComponentRef<Toast> | null = null;
  readonly toastContainer = viewChild.required('toastOutlet', {
    read: ViewContainerRef,
  });
  private toastMessage = signal<string>('');

  async ngOnInit(): Promise<void> {
    try {
      const problems: AllProblems = await this.apiClient.getAllProblems();
      this.allProblems.set(problems.problems);
      this.selectedProblems.set([]);
      this.selectedDifficulty.set(this.DIFFICULTY_OPTIONS[0]);
    } catch (error) {
      console.error('Error loading problems:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  handleDeleteProblem(problemId: string): void {
    // find that problem
    const problemToRemove = this.selectedProblems().find(
      p => p.problemId === problemId
    );

    if (!problemToRemove) return;

    // Remove from selected
    this.selectedProblems.update(current =>
      current.filter(item => item.problemId !== problemId)
    );

    // Add back to all
    this.allProblems.update(current => [...current, problemToRemove]);
  }

  handleAddedProblems(addedProblems: Problem[]): void {
    // Add to Selected Problems
    this.selectedProblems.update(current => [...current, ...addedProblems]);

    // Remove from all
    this.allProblems.update(current =>
      current.filter(
        p => !addedProblems.some(newP => newP.problemId === p.problemId)
      )
    );
  }

  async handleSubmit(event: Event): Promise<void> {
    event.preventDefault();

    const form = event.target as HTMLFormElement;
    const formData = this.extractFormData(form);

    try {
      const userId = this.userId();

      if (!userId) {
        throw new Error('User not authenticated');
      }

      const body = {
        ...formData,
        userId,
      };
      await this.apiClient.createTestSets(body);

      this.router.navigate(['/test-sets/']);
    } catch (error) {
      console.error(`Error creating test set:`, error);
      this.showToast('error', 'Failed to create test set. Please try again.');
    }
  }

  private showToast(type: ToastMessage['type'], message: string): void {
    if (!this.toastRef) {
      this.toastRef = this.toastContainer().createComponent(Toast, {
        bindings: [inputBinding('toastMessage', this.toastMessage)],
      });
    }
    this.toastMessage.set(message);
    this.toastRef.instance.show(type, message);
  }

  private extractFormData(form: HTMLFormElement): TestSetFormData {
    const formData = new FormData(form);

    return {
      title: formData.get('testSetName') as string,
      description: formData.get('testSetDescription') as string,
      difficulty: this.selectedDifficulty().value,
      problems: this.selectedProblems().map(p => ({ problemId: p.problemId })),
    };
  }

  // Modal handlers
  openProblemModal(): void {
    this.showProblemModal.set(true);
  }

  onProblemModalClosed(): void {
    this.showProblemModal.set(false);
  }

  onProblemsAdded(addedProblems: Problem[]): void {
    this.handleAddedProblems(addedProblems);
    this.showProblemModal.set(false);
  }
}
